// Modal for generating, previewing, and downloading/printing reports
import React, { useRef, useState } from 'react';
import { useReports } from '../ReportsProvider';
import '../styles/report-print.css';
import ErrorModal from '../../modals/ErrorModal';
import axios from 'axios';
import { API } from '../../../utils/api';

// Import all tab components to render their A4 pages
import CoverPageTab from '../tabs/CoverPageTab';
import ContentsTab from '../tabs/ContentsTab';
import PhotoSelectionTab from '../tabs/PhotoSelectionTab';
import { 
  ExecutiveSummaryTab,
  ProjectOverviewTab, 
  ProgressHighlightsTab, 
  WorkCompletedTab, 
  QualityInspectionsTab, 
  SafetyComplianceTab,
  SchedulePerformanceTab,
  ResourceUtilizationTab,
  BudgetAnalysisTab,
  RiskAssessmentTab
} from '../tabs/OtherReportTabs';
import { 
  ProgressComparisonsTab,
  TechnicalDrawingsTab,
  NextPhaseTab,
  AppendicesTab
} from '../tabs/OtherReportTabs';

// Renders a section in the preview only when it is included in the report
const PreviewSection = ({ id, isPage = true, children }) => {
  const { isSectionIncluded } = useReports();
  if (!isSectionIncluded(id)) return null;
  return (
    <>
      <div className={isPage ? 'report-page' : undefined} data-section-id={id}>{children}</div>
      <div className="page-spacing"></div>
    </>
  );
};

const ReportGenerationModal = ({ isOpen, onClose, currentEditingReportId, hasUnsavedChanges, onPublishSuccess, currentReportStatus, hasPublishedPdf }) => {
  const printRef = useRef();
  const { reportData, selectedSite, sites, importedPhotos, includedSectionCount } = useReports();
  const [previewPageCount, setPreviewPageCount] = useState(null);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  
  // Overflow warning modal state
  const [overflowWarning, setOverflowWarning] = useState({
    isOpen: false,
    sections: []
  });
  
  // PDF generation progress state
  const [pdfProgress, setPdfProgress] = useState({
    isGenerating: false,
    percent: 0,
    status: ''
  });
  const progressIntervalRef = useRef(null);
  
  // Publish button state
  const [isPublishing, setIsPublishing] = useState(false);
  
  // Download button state
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Determine if publish is allowed
  const canPublish = currentEditingReportId && !hasUnsavedChanges;
  const publishDisabledReason = !currentEditingReportId 
    ? 'Save the report before publishing' 
    : hasUnsavedChanges 
      ? 'Save your changes before publishing' 
      : null;
  
  // Determine if download is allowed (only when published and PDF exists)
  const canDownload = currentReportStatus === 'Published' && hasPublishedPdf;
  const downloadDisabledReason = !currentEditingReportId
    ? 'Save and publish the report first'
    : currentReportStatus !== 'Published'
      ? 'Publish the report to enable download'
      : !hasPublishedPdf
        ? 'No published PDF available'
        : null;

  // Debug logging to check photo availability
  React.useEffect(() => {
    if (isOpen) {
      console.log('ReportGenerationModal opened with photos:', importedPhotos);
      console.log('Photo count:', importedPhotos?.length || 0);
    }
  }, [isOpen, importedPhotos]);

  // Update continuous page numbers after render - only once when modal opens
  const [pageNumbersUpdated, setPageNumbersUpdated] = React.useState(false);
  
  React.useEffect(() => {
    if (isOpen && printRef.current && !pageNumbersUpdated) {
      // Longer delay to ensure all pages are fully rendered
      const timeoutId = setTimeout(() => {
        const pages = printRef.current.querySelectorAll('.a4-page');
        const totalPages = pages.length;
        let globalPageNum = 1;
        
        // Map to track first page of each section for ToC
        const sectionPageMap = {};
        
        console.log(`📄 Found ${totalPages} pages to number`);
        setPreviewPageCount(totalPages);
        
        pages.forEach((page, index) => {
          // Find the section ID for this page by looking up the DOM tree
          let sectionId = null;
          let parent = page.parentElement;
          while (parent && !sectionId) {
            if (parent.hasAttribute('data-section-id')) {
              sectionId = parent.getAttribute('data-section-id');
            }
            parent = parent.parentElement;
          }
          
          // Track the first page of each section for ToC
          if (sectionId && !sectionPageMap[sectionId]) {
            sectionPageMap[sectionId] = globalPageNum;
          }
          
          // Remove any existing footer to avoid duplicates
          const existingFooter = page.querySelector('.page-number-footer');
          if (existingFooter) {
            existingFooter.remove();
          }
          
          // Skip cover page (page 1) - it should not show a number
          if (globalPageNum === 1) {
            globalPageNum++;
            return;
          }
          
          // Create fresh footer element for every page
          const footer = document.createElement('div');
          footer.className = 'page-number-footer';
          footer.style.cssText = `
            position: absolute;
            bottom: 15px;
            right: 30px;
            font-size: 10px;
            color: #6b7280;
            z-index: 10;
          `;
          footer.textContent = `Page ${globalPageNum} of ${totalPages}`;
          
          // Ensure the page has position relative for absolute positioning to work
          if (getComputedStyle(page).position === 'static') {
            page.style.position = 'relative';
          }
          
          page.appendChild(footer);
          console.log(`  Added footer to page ${globalPageNum} (section: ${sectionId || 'unknown'})`);
          
          globalPageNum++;
        });
        
        // Update Table of Contents with page numbers
        console.log('📑 Section page map:', sectionPageMap);
        const tocPageSpans = printRef.current.querySelectorAll('[data-toc-page]');
        tocPageSpans.forEach(span => {
          const sectionId = span.getAttribute('data-toc-page');
          const pageNum = sectionPageMap[sectionId];
          if (pageNum) {
            span.textContent = pageNum;
            console.log(`  ToC: ${sectionId} → Page ${pageNum}`);
          }
        });
        
        console.log(`✅ Finished adding page numbers and updating ToC`);
        setPageNumbersUpdated(true);
      }, 1500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, pageNumbersUpdated]);
  
  // Reset pageNumbersUpdated when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setPageNumbersUpdated(false);
    }
  }, [isOpen]);

  // Cleanup progress interval on unmount
  React.useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Check for overflowing textboxes
  const checkForOverflow = () => {
    if (!printRef.current) return [];
    
    const overflowingElements = printRef.current.querySelectorAll('[data-overflow="true"]');
    const sections = [];
    
    overflowingElements.forEach(element => {
      let sectionName = 'Unknown Section';
      
      // First, try to find the section header (h2) which contains the section number
      // Look in the current report-page container
      const reportPage = element.closest('.report-page');
      if (reportPage) {
        // Look for h2 with section number like "📋 2.0 Executive Summary"
        const sectionHeader = reportPage.querySelector('h2');
        if (sectionHeader) {
          sectionName = sectionHeader.textContent.trim();
          // Remove emoji prefix if present
          sectionName = sectionName.replace(/^[^\d]+(?=\d)/, '').trim();
        }
      }
      
      // If no section header found, try the label above the textbox
      if (sectionName === 'Unknown Section') {
        // Look for immediate parent with label
        const fieldContainer = element.closest('[style*="marginBottom"]') || element.parentElement;
        if (fieldContainer) {
          const label = fieldContainer.querySelector('label, .font-medium');
          if (label) {
            const labelText = label.textContent.trim();
            // Check if there's a section number context
            const pageHeader = reportPage?.querySelector('h2');
            const sectionNum = pageHeader?.textContent.match(/(\d+\.\d+)/)?.[1] || '';
            sectionName = sectionNum ? `${sectionNum} - ${labelText}` : labelText;
          }
        }
      }
      
      // Fallback to editor ID
      if (sectionName === 'Unknown Section') {
        const editorId = element.getAttribute('data-editor-id');
        if (editorId) {
          // Convert camelCase to readable format
          sectionName = editorId
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
        }
      }
      
      if (sectionName && !sections.includes(sectionName)) {
        sections.push(sectionName);
      }
    });
    
    return sections;
  };

  // Simulated progress bar function
  const startProgressSimulation = () => {
    setPdfProgress({ isGenerating: true, percent: 0, status: 'Preparing content...' });
    
    let currentPercent = 0;
    const stages = [
      { target: 20, status: 'Preparing content...', speed: 100 },
      { target: 45, status: 'Extracting styles...', speed: 150 },
      { target: 70, status: 'Generating PDF...', speed: 200 },
      { target: 85, status: 'Processing pages...', speed: 250 },
      { target: 92, status: 'Optimizing...', speed: 300 }
    ];
    let stageIndex = 0;
    
    progressIntervalRef.current = setInterval(() => {
      if (stageIndex < stages.length) {
        const stage = stages[stageIndex];
        if (currentPercent < stage.target) {
          currentPercent += 1;
          setPdfProgress(prev => ({ ...prev, percent: currentPercent, status: stage.status }));
        } else {
          stageIndex++;
        }
      }
    }, 80);
  };
  
  const completeProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setPdfProgress({ isGenerating: true, percent: 100, status: 'Download starting...' });
    setTimeout(() => {
      setPdfProgress({ isGenerating: false, percent: 0, status: '' });
    }, 500);
  };
  
  const cancelProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setPdfProgress({ isGenerating: false, percent: 0, status: '' });
  };

  // Add keyboard event listener for Escape key
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Backdrop click disabled - modal must be closed via buttons
  const handleBackdropClick = (e) => {
    // Click outside to close disabled for safety
  };

  // Get site name for display
  const getSiteName = (siteId) => {
    if (!siteId || !sites || sites.length === 0) {
      return 'Unknown Site';
    }
    const site = sites.find(site => site.id === siteId);
    return site ? site.site_name : 'Unknown Site';
  };

  // Function to convert images to base64 data URLs for PDF compatibility
  const convertImagesToBase64 = async () => {
    if (printRef.current) {
      const images = printRef.current.querySelectorAll('img');
      const conversionPromises = Array.from(images).map(async (img) => {
        if (img.src && !img.src.startsWith('data:')) {
          try {
            // Create a new image element with cross-origin attribute
            const proxyImg = new Image();
            proxyImg.crossOrigin = 'anonymous';
            
            // Wait for image to load
            await new Promise((resolve, reject) => {
              proxyImg.onload = resolve;
              proxyImg.onerror = () => {
                console.warn('Failed to load image with CORS, keeping original URL:', img.src);
                resolve(); // Continue even if CORS fails
              };
              setTimeout(() => {
                console.warn('Image load timeout, keeping original URL:', img.src);
                resolve();
              }, 3000);
              proxyImg.src = img.src;
            });
            
            // Only convert if image loaded successfully
            if (proxyImg.complete && proxyImg.naturalWidth > 0) {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              canvas.width = proxyImg.naturalWidth;
              canvas.height = proxyImg.naturalHeight;
              
              // Draw image to canvas
              ctx.drawImage(proxyImg, 0, 0);
              
              // Convert to base64 data URL
              const dataURL = canvas.toDataURL('image/jpeg', 0.9);
              img.src = dataURL;
              console.log('Successfully converted image to base64');
            }
          } catch (error) {
            console.warn('Failed to convert image to base64:', error);
            // Keep original src as fallback
          }
        }
      });
      
      await Promise.all(conversionPromises);
    }
  };

  // Function to ensure images are loaded before PDF generation
  const waitForImagesToLoad = async () => {
    if (printRef.current) {
      const images = printRef.current.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error to not block PDF generation
            // Fallback timeout in case image doesn't load
            setTimeout(resolve, 2000);
          }
        });
      });
      
      await Promise.all(imagePromises);
    }
  };

  // Function to ensure form values are properly reflected in DOM before PDF generation
  const syncFormValuesToDom = () => {
    if (printRef.current) {
      // Get all input, select, and textarea elements
      const formElements = printRef.current.querySelectorAll('input, select, textarea');
      formElements.forEach(element => {
        if (element.type === 'text' || element.type === 'date' || element.tagName === 'TEXTAREA') {
          // For text inputs and textareas, ensure the value attribute matches the current value
          element.setAttribute('value', element.value);
        } else if (element.tagName === 'SELECT') {
          // For select elements, ensure the selected option is marked as selected
          const selectedOption = element.options[element.selectedIndex];
          if (selectedOption) {
            // Clear all selected attributes first
            Array.from(element.options).forEach(option => option.removeAttribute('selected'));
            // Set the current selection
            selectedOption.setAttribute('selected', 'selected');
          }
        }
      });
    }
  };

  // Handler for initiating PDF download - checks for overflow first
  const handleDownloadClick = async () => {
    // Force all TiptapEditors to re-measure their content heights
    // This ensures overflow detection is accurate even if modal was closed/reopened
    window.dispatchEvent(new Event('resize'));
    
    // Wait for all TiptapEditors to complete their height measurements
    // and update their data-overflow attributes
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const overflowingSections = checkForOverflow();
    
    if (overflowingSections.length > 0) {
      setOverflowWarning({
        isOpen: true,
        sections: overflowingSections
      });
    } else {
      handleDownloadPDF();
    }
  };

  // Handle publish report
  const handlePublishReport = async () => {
    if (!canPublish) return;
    
    try {
      setIsPublishing(true);
      startProgressSimulation();
      
      // Wait for page numbers to be added (they're added via useEffect with 1500ms delay)
      if (!pageNumbersUpdated) {
        console.log('⏳ Waiting for page numbers to be added...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // Wait a moment to ensure React has finished rendering
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Sync form values to DOM attributes
      syncFormValuesToDom();
      
      // Wait for all images to load
      await waitForImagesToLoad();
      
      // Verify page numbers exist before proceeding
      const pageFooters = printRef.current.querySelectorAll('.page-number-footer');
      console.log(`📄 Found ${pageFooters.length} page number footers before cloning`);
      
      // Get all stylesheets from the current document
      const styleSheets = Array.from(document.styleSheets);
      let combinedStyles = '';
      
      for (const sheet of styleSheets) {
        try {
          const rules = Array.from(sheet.cssRules || []);
          combinedStyles += rules.map(rule => rule.cssText).join('\n');
        } catch (e) {
          // Cross-origin stylesheets may throw, skip them
        }
      }
      
      // Clone the content and convert inputs to spans for better PDF rendering
      const clonedContent = printRef.current.cloneNode(true);
      
      // Verify page footers are in the cloned content
      const clonedFooters = clonedContent.querySelectorAll('.page-number-footer');
      console.log(`📄 Found ${clonedFooters.length} page number footers in cloned content`);
      
      // Also check ToC page numbers
      const tocPageSpans = clonedContent.querySelectorAll('[data-toc-page]');
      console.log(`📑 Found ${tocPageSpans.length} ToC page spans in cloned content`);
      tocPageSpans.forEach(span => {
        console.log(`  ToC span: ${span.getAttribute('data-toc-page')} = "${span.textContent}"`);
      });
      
      // Convert all input elements to styled spans with their values
      const inputs = clonedContent.querySelectorAll('input, select, textarea');
      inputs.forEach(input => {
        // Skip range inputs - they have visual representations (progress bars) already
        if (input.type === 'range') {
          // Remove range inputs entirely as they show raw numeric values
          if (input.parentNode) {
            input.parentNode.removeChild(input);
          }
          return;
        }
        
        const span = document.createElement('span');
        // Copy all inline styles from the input
        span.style.cssText = input.style.cssText;
        // Add display block to maintain layout
        span.style.display = input.style.display || 'block';
        span.style.width = input.style.width || '100%';
        span.style.boxSizing = 'border-box';
        // Ensure text is vertically centered
        span.style.lineHeight = '1.4';
        
        // Get the value - handle different input types
        let value = '';
        if (input.tagName === 'SELECT') {
          const selectedOption = input.options[input.selectedIndex];
          value = selectedOption ? selectedOption.text : '';
        } else if (input.type === 'date' && input.value) {
          // Format date nicely
          const date = new Date(input.value);
          value = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        } else {
          value = input.value || input.placeholder || '';
        }
        
        span.textContent = value;
        span.className = input.className;
        
        // Replace input with span
        if (input.parentNode) {
          input.parentNode.replaceChild(span, input);
        }
      });
      
      // Create a complete HTML document
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            ${combinedStyles}
            
            /* Hide UI elements */
            .page-spacing, .modal-controls, button, 
            button[aria-label*="close"], .cursor-pointer:has(svg) {
              display: none !important;
            }
            
            @page {
              size: A4;
              margin: 0;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              background: white;
            }
            
            .a4-page {
              width: 210mm;
              min-height: 297mm;
              padding: 0;
              margin: 0;
              box-sizing: border-box;
              page-break-after: always;
              page-break-inside: avoid;
              position: relative;
              background: white;
            }
            
            .a4-page:last-child {
              page-break-after: auto;
            }
            
            .page-spacing {
              display: none;
            }
            
            img {
              max-width: 100%;
              height: auto;
            }
            
            .tiptap-readonly img {
              max-width: 100%;
              height: auto;
            }
            
            /* Page number footer styling for PDF */
            .page-number-footer {
              position: absolute !important;
              bottom: 15px !important;
              right: 30px !important;
              font-size: 10px !important;
              color: #6b7280 !important;
              z-index: 9999 !important;
              display: block !important;
              visibility: visible !important;
            }
            
            /* Override overflow hidden on A4 pages for PDF generation */
            .a4-page {
              overflow: visible !important;
            }
            
            .a4-page > div {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div class="report-content">
            ${clonedContent.innerHTML}
          </div>
        </body>
        </html>
      `;
      
      // Send to backend for PDF generation and publishing
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/saved-reports/${currentEditingReportId}/publish`,
        { html_content: htmlContent },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 300000  // 5 minute timeout for large reports via API2PDF
        }
      );
      
      completeProgress();
      
      // Notify parent of successful publish
      if (onPublishSuccess && response.data) {
        onPublishSuccess({
          published_pdf_url: response.data.published_pdf_url,
          published_at: response.data.published_at,
          status: response.data.status
        });
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error publishing report:', error);
      cancelProgress();
      setErrorModal({
        isOpen: true,
        message: error.response?.data?.detail || 'Failed to publish report. Please try again.'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!canDownload || !currentEditingReportId) return;
    
    try {
      setIsDownloading(true);
      startProgressSimulation();
      
      // Get auth token
      const token = localStorage.getItem('token');
      
      // Download the published PDF from GCS via backend
      const response = await axios.get(
        `${API}/admin/saved-reports/${currentEditingReportId}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );
      
      // Generate filename
      const siteName = getSiteName(selectedSite).replace(/[^a-zA-Z0-9]/g, '_');
      const date = new Date().toISOString().split('T')[0];
      const filename = `Progress_Report_${siteName}_${date}.pdf`;
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF downloaded successfully from GCS');
      
      // Complete the progress bar
      completeProgress();
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      cancelProgress();
      setErrorModal({
        isOpen: true,
        message: error.response?.data?.detail || 'Error downloading PDF. Please try again.'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      {/* Overflow Warning Modal */}
      {overflowWarning.isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Header */}
            <div className="bg-amber-50 border-b border-amber-200 p-4 flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-800">Content Overflow Detected</h3>
                <p className="text-sm text-amber-600">Some sections have content that exceeds the page boundary</p>
              </div>
            </div>
            
            {/* Warning Body */}
            <div className="p-4">
              <p className="text-gray-700 mb-3">
                The following sections have overflowing text that <strong>will not be visible</strong> in the PDF export:
              </p>
              <ul className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                {overflowWarning.sections.map((section, index) => (
                  <li key={index} className="flex items-center text-red-700 py-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    {section}
                  </li>
                ))}
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Additional content can be continued in a separate textbox on the next page. 
                  Use the <strong>Action Toolbar</strong> to add a new textbox.
                </p>
              </div>
            </div>
            
            {/* Warning Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end space-x-3">
              <button
                onClick={() => setOverflowWarning({ isOpen: false, sections: [] })}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Go Back & Fix
              </button>
              <button
                onClick={handleDownloadPDF}
                className="px-4 py-2 text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
              >
                Download Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">📊 Report Preview</h2>
            <p className="text-gray-600 mt-1">
              {reportData.title || 'Construction Progress Report'} - {getSiteName(selectedSite)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
        </div>

        {/* Modal Controls */}
        <div className="modal-controls flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          {/* Progress Bar - shows when generating */}
          <div className={`flex-1 mr-4 transition-opacity duration-300 ${pdfProgress.isGenerating ? 'opacity-100' : 'opacity-0'}`}>
            {pdfProgress.isGenerating && (
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{pdfProgress.status}</span>
                    <span className="text-sm font-medium text-blue-600">{pdfProgress.percent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-2.5 rounded-full transition-all duration-150 ease-out"
                      style={{ width: `${pdfProgress.percent}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Button Group */}
          <div className="flex items-center space-x-3">
            <span data-testid="publish-summary" className="text-sm text-gray-600 whitespace-nowrap">
              Publishing {includedSectionCount} {includedSectionCount === 1 ? 'section' : 'sections'}
              {previewPageCount ? ` · ${previewPageCount} pages` : ''}
            </span>
            {/* Download Button */}
            <div className="relative group">
              <button
                onClick={async () => {
                  if (canDownload && currentEditingReportId) {
                    try {
                      setIsDownloading(true);
                      const token = localStorage.getItem('token');
                      const response = await axios.get(
                        `${API}/admin/saved-reports/${currentEditingReportId}/download`,
                        {
                          headers: { 'Authorization': `Bearer ${token}` },
                          responseType: 'blob'
                        }
                      );
                      // Create download link
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      const filename = reportData.title ? `${reportData.title.replace(/\s+/g, '_')}.pdf` : 'Report.pdf';
                      link.setAttribute('download', filename);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Download error:', error);
                      setErrorModal({
                        isOpen: true,
                        message: 'Failed to download the report. Please try again.'
                      });
                    } finally {
                      setIsDownloading(false);
                    }
                  }
                }}
                disabled={!canDownload || isDownloading}
                className={`px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 min-w-[140px] justify-center ${
                  canDownload && !isDownloading
                    ? 'bg-cyan-600 hover:bg-cyan-700 hover:shadow-md text-white'
                    : 'bg-gray-300 cursor-not-allowed text-gray-500'
                }`}
              >
                {isDownloading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <span>📥</span>
                    <span>Download</span>
                  </>
                )}
              </button>
              {/* Tooltip for disabled state */}
              {downloadDisabledReason && !isDownloading && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {downloadDisabledReason}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
            
            {/* Publish Button */}
            <div className="relative group">
              <button
                onClick={handlePublishReport}
                disabled={!canPublish || pdfProgress.isGenerating || isPublishing}
                className={`px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 min-w-[160px] justify-center ${
                  !canPublish || pdfProgress.isGenerating || isPublishing
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                } text-white`}
              >
                {pdfProgress.isGenerating || isPublishing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{isPublishing ? 'Publishing...' : 'Generating...'}</span>
                  </>
                ) : (
                  <>
                    <span>📤</span>
                    <span>Publish Report</span>
                  </>
                )}
              </button>
              {/* Tooltip for disabled state */}
              {publishDisabledReason && !pdfProgress.isGenerating && !isPublishing && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {publishDisabledReason}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Report Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
          <div ref={printRef} className="report-content">
            <PreviewSection id="cover-page"><CoverPageTab /></PreviewSection>
            <PreviewSection id="contents"><ContentsTab /></PreviewSection>
            <PreviewSection id="photos" isPage={false}><PhotoSelectionTab /></PreviewSection>
            <PreviewSection id="executive-summary"><ExecutiveSummaryTab /></PreviewSection>
            <PreviewSection id="project-overview"><ProjectOverviewTab /></PreviewSection>
            <PreviewSection id="progress-highlights"><ProgressHighlightsTab /></PreviewSection>
            <PreviewSection id="work-completed"><WorkCompletedTab /></PreviewSection>
            <PreviewSection id="quality-inspections"><QualityInspectionsTab /></PreviewSection>
            <PreviewSection id="safety-compliance"><SafetyComplianceTab /></PreviewSection>
            <PreviewSection id="schedule-performance"><SchedulePerformanceTab /></PreviewSection>
            <PreviewSection id="resource-utilization"><ResourceUtilizationTab /></PreviewSection>
            <PreviewSection id="budget-analysis"><BudgetAnalysisTab /></PreviewSection>
            <PreviewSection id="risk-assessment"><RiskAssessmentTab /></PreviewSection>
            <PreviewSection id="progress-comparisons"><ProgressComparisonsTab /></PreviewSection>
            <PreviewSection id="technical-drawings"><TechnicalDrawingsTab /></PreviewSection>
            <PreviewSection id="next-phase"><NextPhaseTab /></PreviewSection>
            <PreviewSection id="appendices"><AppendicesTab /></PreviewSection>
          </div>
        </div>
      </div>
      
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="PDF Generation Error"
        message={errorModal.message}
      />
    </div>
  );
};

export default ReportGenerationModal;